import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { motion, AnimatePresence } from "framer-motion";
import { AdminSubnav } from "./AdminSubnav";
import { adminFetch } from "../lib/auth";
import {
  Sparkles, Loader2, Trash2, Save, Copy, Check, RefreshCw,
  Languages, Hash, Play, ChevronDown, ChevronUp, List, Plus,
  ExternalLink, Calendar, ChevronRight, Home, Clock, Edit3, Star,
  Trophy, X,
} from "lucide-react";

interface Question {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
}

interface QuestionSet {
  id: string;
  title: string;
  language: string;
  topic: string;
  questionsPerGame: number;
  questionCount: number;
  createdAt: string;
  createdBy?: string;
  createdByName: string;
  isDemo?: boolean;
  // Langues jouables : langue d'origine en premier, puis les traductions.
  languages?: string[];
  highscoreName?: string | null;
  highscoreValue?: number | null;
}

// Doit rester aligne sur TRANSLATABLE_LANGUAGES dans server/index.js.
const TRANSLATABLE = ["French", "German", "English"];

// Code court affiche dans les badges de langue.
const LANG_CODE: Record<string, string> = {
  French: "FR", German: "DE", English: "EN",
  Luxembourgish: "LU", Portuguese: "PT",
};

type Step = "list" | "generate" | "edit" | "share";

// allMode = true : page /admin/all-sets, on liste TOUS les sets (admin only). On
// affiche le propriétaire sur chaque row et on retire le CTA "New Question Set"
// (les admins créent leurs propres sets via /admin/sets pour rester dans leur scope).
export function AdminPage({ allMode = false }: { allMode?: boolean } = {}) {
  const [step, setStep] = useState<Step>("list");
  const [title, setTitle] = useState("");
  const [language, setLanguage] = useState("French");
  const [topic, setTopic] = useState("");
  const [count, setCount] = useState(20);
  const [questionsPerGame, setQuestionsPerGame] = useState(12);
  const [timePerQuestion, setTimePerQuestion] = useState(20);
  const [questions, setQuestions] = useState<Question[]>([]);
  // Id du set en cours d'édition. Présent => /api/admin/save fait un update sur cet
  // id au lieu de créer un nouveau set (sinon chaque "Save & Share" dupliquait).
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [shareId, setShareId] = useState("");
  const [copied, setCopied] = useState(false);
  const [expandedQ, setExpandedQ] = useState<number | null>(null);
  const [sets, setSets] = useState<QuestionSet[]>([]);
  const [setsLoading, setSetsLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // Highscore inline editor : id du set en cours d'édition + buffer name/value.
  // Affiché en dessous de la row quand editingScore === s.id.
  const [editingScore, setEditingScore] = useState<string | null>(null);
  const [scoreName, setScoreName] = useState("");
  const [scoreValue, setScoreValue] = useState("");

  // --- Load sets ---
  // allMode → /api/admin/all-sets (tous les sets, admin only côté serveur).
  // Sinon → /api/admin/sets (mes sets uniquement).
  const loadSets = useCallback(async () => {
    setSetsLoading(true);
    try {
      const url = allMode ? "/api/admin/all-sets" : "/api/admin/sets";
      const res = await adminFetch(url);
      if (res?.ok) setSets(await res.json());
    } catch { /* ignore */ }
    finally { setSetsLoading(false); }
  }, [allMode]);

  useEffect(() => { loadSets(); }, [loadSets]);

  // Récup du flag isAdmin (vient de ADMIN_USER_IDS env côté serveur).
  // Conditionne l'affichage du toggle "Demo" sur les rows.
  useEffect(() => {
    (async () => {
      const res = await adminFetch("/api/admin/me");
      if (!res?.ok) return;
      const data = await res.json();
      setIsAdmin(!!data.isAdmin);
    })().catch(() => {});
  }, []);

  // --- Highscore inline ---
  // Ouvre l'éditeur sur la row du set choisi en pré-remplissant avec le score actuel
  // (vide si aucun encore). Un seul score édité à la fois (le précédent se ferme).
  const startEditScore = (s: QuestionSet) => {
    setEditingScore(s.id);
    setScoreName(s.highscoreName || "");
    setScoreValue(s.highscoreValue != null ? String(s.highscoreValue) : "");
  };

  const cancelEditScore = () => {
    setEditingScore(null);
    setScoreName("");
    setScoreValue("");
  };

  // Save : si name est vide on efface le score (name=null, value=null côté serveur).
  // Sinon on push name + value(parsed). Optimistic update pour réactivité immédiate.
  const handleSaveHighscore = async (id: string) => {
    const name = scoreName.trim();
    const value = name && scoreValue !== "" ? Number(scoreValue) : null;
    try {
      const res = await adminFetch(`/api/admin/sets/${id}/highscore`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name || null, value }),
      });
      if (!res?.ok) return;
      const data = await res.json();
      setSets((prev) => prev.map((s) => (s.id === id
        ? { ...s, highscoreName: data.highscoreName, highscoreValue: data.highscoreValue }
        : s
      )));
      cancelEditScore();
    } catch { /* ignore */ }
  };

  // --- Traductions ---
  // Un set est genere dans une langue ; les autres sont produites a la demande et
  // stockees a part. Le joueur ne voit un selecteur de langue que si le set en a
  // plusieurs, donc sans ce panneau le multilingue reste inerte.
  const [langPanelId, setLangPanelId] = useState<string | null>(null);
  const [langTargets, setLangTargets] = useState<string[]>([]);
  const [translatingId, setTranslatingId] = useState<string | null>(null);
  const [translateError, setTranslateError] = useState<string | null>(null);

  const openLangPanel = (set: QuestionSet) => {
    setTranslateError(null);
    if (langPanelId === set.id) { setLangPanelId(null); return; }
    setLangPanelId(set.id);
    // Preselectionne les langues manquantes : c'est le cas d'usage courant.
    const have = set.languages || [set.language];
    setLangTargets(TRANSLATABLE.filter((l) => !have.includes(l)));
  };

  const handleTranslate = async (id: string) => {
    if (langTargets.length === 0) return;
    setTranslatingId(id);
    setTranslateError(null);
    try {
      const res = await adminFetch(`/api/admin/sets/${id}/translate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ languages: langTargets }),
      });
      const data = await res?.json().catch(() => null);
      if (!res?.ok || !data) {
        setTranslateError(data?.error || "Translation failed");
        return;
      }
      setSets((prev) => prev.map((x) => (x.id === id ? { ...x, languages: data.languages } : x)));
      if (data.failed?.length) {
        setTranslateError(
          data.failed.map((f: { language: string; reason: string }) => `${f.language}: ${f.reason}`).join(" · ")
        );
      } else {
        setLangPanelId(null);
      }
    } catch (err) {
      setTranslateError(err instanceof Error ? err.message : "Translation failed");
    } finally {
      setTranslatingId(null);
    }
  };

  const handleRemoveTranslation = async (id: string, language: string) => {
    try {
      const res = await adminFetch(
        `/api/admin/sets/${id}/translations/${encodeURIComponent(language)}`,
        { method: "DELETE" }
      );
      const data = await res?.json().catch(() => null);
      if (!res?.ok || !data) return;
      setSets((prev) => prev.map((x) => (x.id === id ? { ...x, languages: data.languages } : x)));
    } catch { /* ignore */ }
  };

  // Toggle is_demo sur un set. Admin only côté serveur, mais on n'affiche le bouton
  // que si isAdmin pour éviter le faux signal côté UI.
  const handleToggleDemo = async (id: string, current: boolean) => {
    try {
      const res = await adminFetch(`/api/admin/sets/${id}/demo`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isDemo: !current }),
      });
      if (!res?.ok) return;
      // Update local en place pour réactivité immédiate (vs refetch complet)
      setSets((prev) => prev.map((s) => (s.id === id ? { ...s, isDemo: !current } : s)));
    } catch { /* ignore */ }
  };

  const shareLink = shareId
    ? `${window.location.origin}/auto-room?questions=${shareId}`
    : "";

  // --- Generate ---
  const handleGenerate = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminFetch("/api/admin/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ language, count, topic }),
      });
      if (!res) return; // session expirée : nav vers Authentik en cours
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.details || err.error || "Generation failed");
      }
      const data = await res.json();
      setQuestions(data.questions);
      setQuestionsPerGame(Math.min(12, data.questions.length));
      setStep("edit");
    } catch (err: any) {
      setError(err.message || "Failed to generate questions");
    } finally {
      setLoading(false);
    }
  };

  // --- Save ---
  const handleSave = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminFetch("/api/admin/save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: editingId, questions, questionsPerGame, timePerQuestion, language, topic, title }),
      });
      if (!res) return; // session expirée : nav vers Authentik en cours
      if (!res.ok) throw new Error("Save failed");
      const data = await res.json();
      setShareId(data.id);
      setStep("share");
      loadSets();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- Delete set ---
  const handleDeleteSet = async (id: string) => {
    if (!confirm("Delete this question set?")) return;
    try {
      await adminFetch(`/api/admin/sets/${id}`, { method: "DELETE" });
      loadSets();
    } catch { /* ignore */ }
  };

  // --- Edit existing set ---
  // Langue actuellement ouverte dans l'editeur. null = langue d'origine du set.
  // Editer une traduction ne passe PAS par /api/admin/save : celui-ci ecrase
  // questions_json et efface toutes les traductions, il detruirait donc justement
  // ce qu'on essaie de modifier.
  const [editingLanguage, setEditingLanguage] = useState<string | null>(null);
  const [editSetLanguages, setEditSetLanguages] = useState<string[]>([]);
  const [editBaseQuestions, setEditBaseQuestions] = useState<Question[]>([]);
  const [editTranslations, setEditTranslations] = useState<Record<string, Question[]>>({});

  const handleEditSet = async (id: string) => {
    try {
      const res = await fetch(`/api/public/questions/${id}`);
      if (!res.ok) throw new Error("Not found");
      const data = await res.json();
      setQuestions(data.questions || []);
      setEditBaseQuestions(data.questions || []);
      setEditTranslations(data.translations || {});
      setEditSetLanguages(data.languages || [data.language || "French"]);
      setEditingLanguage(null);
      setQuestionsPerGame(data.questionsPerGame || 12);
      setTimePerQuestion(data.timePerQuestion || 20);
      setLanguage(data.language || "French");
      setTopic(data.topic || "");
      setTitle(data.title || "");
      setEditingId(id);
      setStep("edit");
    } catch { setError("Failed to load set"); }
  };

  // Bascule d'onglet de langue. On memorise les modifications en cours dans le
  // buffer de la langue qu'on quitte, pour ne pas les perdre en naviguant.
  const switchEditLanguage = (target: string | null) => {
    if (target === editingLanguage) return;
    if (editingLanguage === null) setEditBaseQuestions(questions);
    else setEditTranslations((prev) => ({ ...prev, [editingLanguage]: questions }));
    setQuestions(target === null ? editBaseQuestions : editTranslations[target] || []);
    setEditingLanguage(target);
    setError("");
  };

  // Sauvegarde d'une traduction : endpoint dedie, structure imposee par la langue
  // d'origine cote serveur.
  const handleSaveTranslation = async () => {
    if (!editingId || !editingLanguage) return;
    setLoading(true);
    setError("");
    try {
      const res = await adminFetch(
        `/api/admin/sets/${editingId}/translations/${encodeURIComponent(editingLanguage)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ questions }),
        }
      );
      const data = await res?.json().catch(() => null);
      if (!res?.ok) {
        setError(data?.error || "Failed to save translation");
        return;
      }
      setEditTranslations((prev) => ({ ...prev, [editingLanguage]: questions }));
      setStep("list");
      loadSets();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save translation");
    } finally {
      setLoading(false);
    }
  };

  // --- Question editing ---
  const updateQuestion = (index: number, field: string, value: any) => {
    setQuestions((prev) =>
      prev.map((q, i) => (i === index ? { ...q, [field]: value } : q))
    );
  };

  const updateOption = (qIndex: number, optIndex: number, value: string) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === qIndex
          ? { ...q, options: q.options.map((o, oi) => (oi === optIndex ? value : o)) }
          : q
      )
    );
  };

  const deleteQuestion = (index: number) => {
    // Sur une traduction, la structure est celle de la langue d'origine : retirer
    // une question la desalignerait et le serveur refuserait la sauvegarde.
    if (editingLanguage) return;
    setQuestions((prev) => prev.filter((_, i) => i !== index).map((q, i) => ({ ...q, id: i + 1 })));
  };

  const copyLink = async (link?: string) => {
    try {
      await navigator.clipboard.writeText(link || shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const goToNew = () => {
    setQuestions([]);
    setShareId("");
    setError("");
    setExpandedQ(null);
    setEditingId(null);
    setStep("generate");
  };

  return (
    <div className="min-h-screen font-kawaii bg-gradient-to-br from-sakura-100 via-lavender-100 to-sky-100 relative overflow-hidden">
      <div className="absolute top-[-15%] left-[-10%] w-[350px] h-[350px] rounded-full bg-sakura-200/40 blur-3xl" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[300px] h-[300px] rounded-full bg-lavender-200/40 blur-3xl" />

      <div className="relative z-10 max-w-3xl mx-auto p-4 py-8">
        {/* Breadcrumb */}
        <motion.nav
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="mb-6 flex items-center gap-1 text-sm font-bold"
        >
          <Link
            to="/"
            className="flex items-center gap-1 text-lavender-400 hover:text-sakura-400 transition-colors"
          >
            <Home className="w-4 h-4" />
            Home
          </Link>
          <ChevronRight className="w-4 h-4 text-lavender-300" />
          <Link
            to="/admin"
            className="text-lavender-400 hover:text-sakura-400 transition-colors"
          >
            Admin
          </Link>
          <ChevronRight className="w-4 h-4 text-lavender-300" />
          <button
            onClick={() => { setStep("list"); setError(""); }}
            className={`transition-colors ${step === "list" ? "text-sakura-500" : "text-lavender-400 hover:text-sakura-400"}`}
          >
            Question Sets
          </button>
          {step !== "list" && (
            <>
              <ChevronRight className="w-4 h-4 text-lavender-300" />
              <button
                onClick={() => { if (step === "edit" || step === "share") { setStep("generate"); setError(""); } }}
                className={`transition-colors ${step === "generate" ? "text-sakura-500" : step === "edit" || step === "share" ? "text-lavender-400 hover:text-sakura-400" : "text-lavender-300"}`}
              >
                {topic || "New Set"}
              </button>
            </>
          )}
          {(step === "edit" || step === "share") && (
            <>
              <ChevronRight className="w-4 h-4 text-lavender-300" />
              <button
                onClick={() => { if (step === "share") { setStep("edit"); setShareId(""); setError(""); } }}
                className={`transition-colors ${step === "edit" ? "text-sakura-500" : "text-lavender-400 hover:text-sakura-400"}`}
              >
                Edit
              </button>
            </>
          )}
          {step === "share" && (
            <>
              <ChevronRight className="w-4 h-4 text-lavender-300" />
              <span className="text-sakura-500">Share</span>
            </>
          )}
        </motion.nav>

        <AdminSubnav isAdmin={isAdmin} />

        {/* Error */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-4 p-3 bg-red-50 border-2 border-red-200 rounded-xl text-red-600 font-bold text-sm text-center"
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step: List (My Sets) */}
        {step === "list" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-4"
          >
            {/* New set button — caché en allMode (les admins créent via /admin/sets) */}
            {!allMode && (
              <motion.button
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.97 }}
                onClick={goToNew}
                className="w-full py-4 text-white font-black text-lg bg-gradient-to-r from-sakura-400 via-sakura-500 to-lavender-400 rounded-xl shadow-kawaii-lg flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" />
                New Question Set
              </motion.button>
            )}

            {/* Sets list */}
            <div className="bg-white/80 backdrop-blur-xl rounded-blob shadow-kawaii-lg border-2 border-sakura-200/50 p-5">
              <div className="flex items-center gap-2 mb-4">
                <List className="w-5 h-5 text-lavender-500" />
                <h2 className="text-lg font-black text-lavender-500">
                  {allMode ? "All Question Sets" : "My Question Sets"}
                </h2>
                {allMode && (
                  <span className="ml-auto text-[10px] font-black uppercase tracking-wider text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                    Super-admin
                  </span>
                )}
              </div>

              {setsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-sakura-400" />
                </div>
              ) : sets.length === 0 ? (
                <p className="text-center text-gray-400 font-bold text-sm py-8">
                  No question sets yet. Create your first one!
                </p>
              ) : (
                <div className="space-y-3">
                  {sets.map((s) => {
                    const setLink = `${window.location.origin}/auto-room?questions=${s.id}`;
                    return (
                      <motion.div
                        key={s.id}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 bg-white/70 rounded-xl border-2 border-sakura-100 hover:border-sakura-200 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-black text-gray-700 text-sm truncate">
                              {s.title || s.topic || "Untitled"}{" "}
                              <span className="font-bold text-lavender-400">({s.language})</span>
                            </p>
                            <p className="text-xs text-gray-400 font-bold mt-1 flex items-center gap-2 flex-wrap">
                              <span>{s.questionCount} questions ({s.questionsPerGame}/game)</span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(s.createdAt).toLocaleDateString()}
                              </span>
                              {allMode && s.createdByName && (
                                <span className="flex items-center gap-1 text-lavender-500 bg-lavender-50 px-1.5 py-0.5 rounded">
                                  <span className="opacity-60">by</span> {s.createdByName}
                                </span>
                              )}
                              {s.highscoreName && s.highscoreValue != null && editingScore !== s.id && (
                                <span className="flex items-center gap-1 text-amber-600">
                                  <Trophy className="w-3 h-3" />
                                  {s.highscoreName} : {s.highscoreValue.toLocaleString()}
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {isAdmin && (
                              <button
                                onClick={() => handleToggleDemo(s.id, !!s.isDemo)}
                                className={`p-2 rounded-lg transition-colors ${
                                  s.isDemo
                                    ? "text-amber-500 hover:bg-amber-50"
                                    : "text-gray-300 hover:text-amber-400 hover:bg-amber-50"
                                }`}
                                title={s.isDemo ? "Visible sur la home (cliquer pour masquer)" : "Cliquer pour afficher sur la home"}
                              >
                                <Star className={`w-4 h-4 ${s.isDemo ? "fill-current" : ""}`} />
                              </button>
                            )}
                            <button
                              onClick={() => openLangPanel(s)}
                              className={`p-2 rounded-lg transition-colors ${
                                (s.languages?.length || 1) > 1
                                  ? "text-emerald-500 hover:bg-emerald-50"
                                  : "text-gray-300 hover:text-emerald-400 hover:bg-emerald-50"
                              }`}
                              title={
                                (s.languages?.length || 1) > 1
                                  ? `Available in ${(s.languages || []).join(", ")}`
                                  : "Translate this quiz"
                              }
                            >
                              <Languages className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => startEditScore(s)}
                              className={`p-2 rounded-lg transition-colors ${
                                s.highscoreName
                                  ? "text-amber-500 hover:bg-amber-50"
                                  : "text-gray-300 hover:text-amber-400 hover:bg-amber-50"
                              }`}
                              title={s.highscoreName ? "Modifier le highscore" : "Ajouter un highscore"}
                            >
                              <Trophy className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => copyLink(setLink)}
                              className="p-2 text-lavender-400 hover:bg-lavender-50 rounded-lg transition-colors"
                              title="Copy link"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <a
                              href={`/auto-room?questions=${s.id}`}
                              target="_blank"
                              rel="noopener"
                              className="p-2 text-sakura-400 hover:bg-sakura-50 rounded-lg transition-colors"
                              title="Open game"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                            <button
                              onClick={() => handleEditSet(s.id)}
                              className="p-2 text-lavender-500 hover:bg-lavender-100 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteSet(s.id)}
                              className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        {/* Panneau de traduction. Les langues deja presentes sont
                            listees en badges ; la langue d'origine n'est pas
                            supprimable (elle vit dans questions_json). */}
                        {langPanelId === s.id && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mt-3 pt-3 border-t border-sakura-100 space-y-3"
                          >
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-[11px] font-black uppercase tracking-wider text-gray-400">
                                Available
                              </span>
                              {(s.languages || [s.language]).map((lang, i) => (
                                <span
                                  key={lang}
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-black ${
                                    i === 0
                                      ? "bg-lavender-100 text-lavender-600"
                                      : "bg-emerald-50 text-emerald-600"
                                  }`}
                                >
                                  {LANG_CODE[lang] || lang.slice(0, 2).toUpperCase()}
                                  {i === 0 ? (
                                    <span className="text-[10px] opacity-60">original</span>
                                  ) : (
                                    <button
                                      onClick={() => handleRemoveTranslation(s.id, lang)}
                                      className="hover:text-red-500 transition-colors"
                                      title={`Remove ${lang} translation`}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  )}
                                </span>
                              ))}
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-[11px] font-black uppercase tracking-wider text-gray-400">
                                Translate to
                              </span>
                              {TRANSLATABLE.filter((l) => l !== s.language).map((lang) => {
                                const active = langTargets.includes(lang);
                                return (
                                  <button
                                    key={lang}
                                    onClick={() =>
                                      setLangTargets((prev) =>
                                        prev.includes(lang)
                                          ? prev.filter((x) => x !== lang)
                                          : [...prev, lang]
                                      )
                                    }
                                    className={`px-3 py-1.5 rounded-lg text-xs font-black border-2 transition-colors ${
                                      active
                                        ? "bg-emerald-500 text-white border-emerald-500"
                                        : "bg-white text-gray-500 border-gray-200 hover:border-emerald-300"
                                    }`}
                                  >
                                    {lang}
                                  </button>
                                );
                              })}
                              <button
                                onClick={() => handleTranslate(s.id)}
                                disabled={translatingId === s.id || langTargets.length === 0}
                                className="ml-auto inline-flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-black bg-lavender-500 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-lavender-400 transition-colors"
                              >
                                {translatingId === s.id ? (
                                  <>
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    Translating...
                                  </>
                                ) : (
                                  <>
                                    <Languages className="w-3.5 h-3.5" />
                                    Translate
                                  </>
                                )}
                              </button>
                            </div>

                            {/* Une traduction = plusieurs appels LLM (lots de 8
                                questions), comptez une dizaine de secondes par
                                langue. Sans ce repere on croit a un plantage. */}
                            {translatingId === s.id && (
                              <p className="text-[11px] font-bold text-lavender-400">
                                {s.questionCount} questions to translate. This takes a few
                                seconds per language, keep this page open.
                              </p>
                            )}
                            {translateError && langPanelId === s.id && (
                              <p className="text-[11px] font-bold text-red-500 break-words">
                                {translateError}
                              </p>
                            )}
                            <p className="text-[11px] font-bold text-gray-400">
                              Editing the questions clears the translations: they are
                              index-aligned, so a change would silently desync them.
                            </p>
                          </motion.div>
                        )}

                        {/* Highscore inline editor : nom (texte) + score (number) + save / cancel.
                            Vider le nom puis Save = efface le highscore (cf. setHighscore côté serveur). */}
                        {editingScore === s.id && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mt-3 pt-3 border-t border-sakura-100 flex items-center gap-2"
                          >
                            <Trophy className="w-4 h-4 text-amber-500 shrink-0" />
                            <input
                              type="text"
                              value={scoreName}
                              onChange={(e) => setScoreName(e.target.value)}
                              onKeyDown={(e) => e.key === "Enter" && handleSaveHighscore(s.id)}
                              placeholder="Name (e.g. Pierre)"
                              maxLength={32}
                              className="flex-1 min-w-0 px-2.5 py-1.5 text-xs font-bold bg-white border-2 border-sakura-100 rounded-lg focus:outline-none focus:border-sakura-300"
                            />
                            <span className="text-gray-400 font-black">:</span>
                            <input
                              type="number"
                              value={scoreValue}
                              onChange={(e) => setScoreValue(e.target.value)}
                              onKeyDown={(e) => e.key === "Enter" && handleSaveHighscore(s.id)}
                              placeholder="0"
                              min={0}
                              className="w-24 px-2.5 py-1.5 text-xs font-bold bg-white border-2 border-sakura-100 rounded-lg focus:outline-none focus:border-sakura-300"
                            />
                            <button
                              onClick={() => handleSaveHighscore(s.id)}
                              className="p-1.5 text-emerald-500 hover:bg-emerald-50 rounded-lg transition-colors"
                              title="Save highscore"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={cancelEditScore}
                              className="p-1.5 text-gray-400 hover:bg-gray-50 rounded-lg transition-colors"
                              title="Cancel"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </motion.div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Step: Generate */}
        {step === "generate" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-4"
          >
            <div className="bg-white/80 backdrop-blur-xl rounded-blob shadow-kawaii-lg border-2 border-sakura-200/50 p-6 space-y-5">
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-lavender-500 mb-2">
                  <Sparkles className="w-4 h-4" />
                  Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. History Quiz — French Revolution"
                  className="w-full px-4 py-3 border-2 border-sakura-200 rounded-xl font-bold text-gray-700 bg-white/70 focus:outline-none focus:ring-2 focus:ring-sakura-300 placeholder:text-sakura-200"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-lavender-500 mb-2">
                  <Languages className="w-4 h-4" />
                  Language
                </label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-sakura-200 rounded-xl font-bold text-gray-700 bg-white/70 focus:outline-none focus:ring-2 focus:ring-sakura-300"
                >
                  <option value="French">French</option>
                  <option value="German">German</option>
                  <option value="Luxembourgish">Luxembourgish</option>
                  <option value="English">English</option>
                  <option value="Portuguese">Portuguese</option>
                </select>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-lavender-500 mb-2">
                  <Sparkles className="w-4 h-4" />
                  Topic
                </label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g. Ancient Egypt, easy level, 8-10 year-olds"
                  className="w-full px-4 py-3 border-2 border-sakura-200 rounded-xl font-bold text-gray-700 bg-white/70 focus:outline-none focus:ring-2 focus:ring-sakura-300 placeholder:text-sakura-200"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-lavender-500 mb-2">
                  <Hash className="w-4 h-4" />
                  Number of questions
                </label>
                <input
                  type="number"
                  min={5}
                  max={50}
                  value={count}
                  onChange={(e) => setCount(Math.max(5, Math.min(50, Number(e.target.value))))}
                  className="w-full px-4 py-3 border-2 border-sakura-200 rounded-xl font-bold text-gray-700 bg-white/70 focus:outline-none focus:ring-2 focus:ring-sakura-300"
                />
              </div>

              <motion.button
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleGenerate}
                disabled={loading}
                className="w-full py-4 text-white font-black text-lg bg-gradient-to-r from-sakura-400 via-sakura-500 to-lavender-400 rounded-xl shadow-kawaii-lg disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Generate Questions
                  </>
                )}
              </motion.button>

              {loading && (
                <p className="text-center text-lavender-400 text-sm font-bold animate-pulse">
                  The AI is crafting your questions... This may take up to 60 seconds.
                </p>
              )}

              {/* Separator */}
              <div className="flex items-center gap-3 pt-2">
                <div className="flex-1 h-px bg-sakura-200/50" />
                <span className="text-xs font-bold text-lavender-300">or</span>
                <div className="flex-1 h-px bg-sakura-200/50" />
              </div>

              {/* Manual creation */}
              <motion.button
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => {
                  setQuestions([
                    { id: 1, question: "", options: ["", "", ""], correctAnswer: 0 },
                  ]);
                  setExpandedQ(0);
                  setStep("edit");
                }}
                disabled={loading}
                className="w-full py-4 text-lavender-500 font-black text-lg bg-white/60 backdrop-blur rounded-xl border-2 border-dashed border-lavender-300 hover:bg-lavender-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                Create questions manually
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Step: Edit */}
        {step === "edit" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            {/* Back button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setStep("list")}
              className="text-sm font-bold text-lavender-400 hover:text-lavender-600 flex items-center gap-1"
            >
              ← Back to my sets
            </motion.button>

            {/* Title & Topic — éditables ici aussi (l'aperçu de la home utilise ces
                champs sur les sets is_demo, voir WelcomeScreen.tsx ~l.385). */}
            <div className="bg-white/80 backdrop-blur-xl rounded-xl shadow-kawaii-card border-2 border-sakura-200/50 p-4 space-y-3">
              <div>
                <label className="flex items-center gap-2 text-xs font-bold text-lavender-500 mb-1">
                  <Sparkles className="w-3.5 h-3.5" />
                  Title <span className="font-normal text-lavender-300">— visible sur la home si marqué demo</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. History Quiz — French Revolution"
                  className="w-full px-3 py-2 border-2 border-sakura-200 rounded-lg font-bold text-sm text-gray-700 bg-white/70 focus:outline-none focus:ring-2 focus:ring-sakura-300 placeholder:text-sakura-200"
                />
              </div>
              <div>
                <label className="flex items-center gap-2 text-xs font-bold text-lavender-500 mb-1">
                  <Sparkles className="w-3.5 h-3.5" />
                  Topic / prompt <span className="font-normal text-lavender-300">— sous-titre de la carte home + base du prompt LLM si régénération</span>
                </label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g. Ancient Egypt, easy level, 8-10 year-olds"
                  className="w-full px-3 py-2 border-2 border-sakura-200 rounded-lg font-bold text-sm text-gray-700 bg-white/70 focus:outline-none focus:ring-2 focus:ring-sakura-300 placeholder:text-sakura-200"
                />
              </div>
            </div>

            {/* Controls bar */}
            <div className="bg-white/80 backdrop-blur-xl rounded-xl shadow-kawaii-card border-2 border-sakura-200/50 p-4 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Play className="w-4 h-4 text-lavender-500" />
                <label className="text-sm font-bold text-lavender-500">Per game:</label>
                <input
                  type="number"
                  min={1}
                  max={questions.length}
                  value={questionsPerGame}
                  onChange={(e) => setQuestionsPerGame(Math.max(1, Math.min(questions.length, Number(e.target.value))))}
                  className="w-16 px-2 py-1 border-2 border-lavender-200 rounded-lg font-bold text-center text-gray-700 bg-white/70"
                />
                <span className="text-sm text-gray-400">/ {questions.length}</span>
              </div>

              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-sakura-500" />
                <label className="text-sm font-bold text-sakura-500">Time / question:</label>
                <input
                  type="number"
                  min={5}
                  max={120}
                  value={timePerQuestion}
                  onChange={(e) => setTimePerQuestion(Math.max(5, Math.min(120, Number(e.target.value))))}
                  className="w-16 px-2 py-1 border-2 border-sakura-200 rounded-lg font-bold text-center text-gray-700 bg-white/70"
                />
                <span className="text-sm text-gray-400">sec</span>
              </div>
              <div className="flex gap-2 ml-auto">
                {/* Regenerate ne vaut que pour la langue d'origine : relancer une
                    generation depuis un onglet de traduction n'aurait pas de sens,
                    la traduction derive de l'original. */}
                {!editingLanguage && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={goToNew}
                    className="px-4 py-2 text-sm font-bold text-lavender-500 bg-lavender-50 rounded-lg border-2 border-lavender-200 flex items-center gap-1"
                  >
                    <RefreshCw className="w-3 h-3" /> Regenerate
                  </motion.button>
                )}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={editingLanguage ? handleSaveTranslation : handleSave}
                  disabled={loading || questions.length === 0}
                  className="px-4 py-2 text-sm font-black text-white bg-gradient-to-r from-sakura-400 to-lavender-400 rounded-lg shadow-kawaii flex items-center gap-1 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  {editingLanguage ? `Save ${editingLanguage}` : "Save & Share"}
                </motion.button>
              </div>
            </div>

            {/* Onglets de langue. Affiches seulement si le set est multilingue.
                L'onglet actif determine quelles questions sont dans l'editeur ET
                vers quel endpoint part la sauvegarde. */}
            {editSetLanguages.length > 1 && (
              <div className="flex flex-wrap items-center gap-2 bg-white/80 backdrop-blur-xl rounded-xl border-2 border-sakura-200/50 p-3">
                <Languages className="w-4 h-4 text-lavender-400 shrink-0" />
                {editSetLanguages.map((lang, i) => {
                  const isBase = i === 0;
                  const active = isBase ? editingLanguage === null : editingLanguage === lang;
                  return (
                    <button
                      key={lang}
                      onClick={() => switchEditLanguage(isBase ? null : lang)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-black border-2 transition-colors ${
                        active
                          ? "bg-lavender-500 text-white border-lavender-500"
                          : "bg-white text-gray-500 border-gray-200 hover:border-lavender-300"
                      }`}
                    >
                      {lang}
                      {isBase && <span className="ml-1.5 opacity-60">original</span>}
                    </button>
                  );
                })}
                {editingLanguage && (
                  <p className="w-full text-[11px] font-bold text-gray-400">
                    Translation of the original. Only the texts are editable: the number
                    of questions, the number of options and the correct answer stay those
                    of {editSetLanguages[0]}, otherwise players in different languages
                    would stop playing the same game.
                  </p>
                )}
              </div>
            )}

            {/* Question list */}
            {questions.map((q, qi) => (
              <motion.div
                key={q.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: qi * 0.02 }}
                className="bg-white/80 backdrop-blur-xl rounded-xl shadow-kawaii-card border-2 border-sakura-200/50 overflow-hidden"
              >
                {/* Question header (collapsible) */}
                <div
                  className="p-4 flex items-start gap-3 cursor-pointer hover:bg-sakura-50/50 transition-colors"
                  onClick={() => setExpandedQ(expandedQ === qi ? null : qi)}
                >
                  <span className="shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-sakura-400 to-lavender-400 text-white font-black text-sm flex items-center justify-center">
                    {qi + 1}
                  </span>
                  <p className="flex-1 font-bold text-gray-700 text-sm leading-snug">
                    {q.question || "(empty)"}
                  </p>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      hidden={!!editingLanguage}
                      onClick={(e) => { e.stopPropagation(); deleteQuestion(qi); }}
                      className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    {expandedQ === qi
                      ? <ChevronUp className="w-4 h-4 text-gray-400" />
                      : <ChevronDown className="w-4 h-4 text-gray-400" />
                    }
                  </div>
                </div>

                {/* Expanded editor */}
                <AnimatePresence>
                  {expandedQ === qi && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 space-y-3 border-t border-sakura-100">
                        {/* Question text */}
                        <textarea
                          value={q.question}
                          onChange={(e) => updateQuestion(qi, "question", e.target.value)}
                          rows={2}
                          className="w-full mt-3 px-3 py-2 border-2 border-sakura-200 rounded-lg text-sm font-bold text-gray-700 bg-white/70 focus:outline-none focus:ring-2 focus:ring-sakura-300 resize-none"
                        />

                        {/* Options */}
                        {q.options.map((opt, oi) => (
                          <div key={oi} className="flex items-center gap-2">
                            <button
                              onClick={() => { if (!editingLanguage) updateQuestion(qi, "correctAnswer", oi); }}
                              className={`shrink-0 w-8 h-8 rounded-lg font-black text-sm flex items-center justify-center transition-all ${
                                q.correctAnswer === oi
                                  ? "bg-emerald-500 text-white shadow-sm"
                                  : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                              }`}
                            >
                              {String.fromCharCode(65 + oi)}
                            </button>
                            <input
                              type="text"
                              value={opt}
                              onChange={(e) => updateOption(qi, oi, e.target.value)}
                              className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg text-sm font-bold text-gray-700 bg-white/70 focus:outline-none focus:ring-2 focus:ring-sakura-300"
                            />
                            {q.correctAnswer === oi && (
                              <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                            )}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}

            {/* Add question manually */}
            <motion.button
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.97 }}
              hidden={!!editingLanguage}
              onClick={() => {
                const newQ: Question = {
                  id: questions.length + 1,
                  question: "",
                  options: ["", "", ""],
                  correctAnswer: 0,
                };
                setQuestions([...questions, newQ]);
                setExpandedQ(questions.length);
              }}
              className="w-full py-3 text-sm font-bold text-sakura-500 bg-white/60 backdrop-blur rounded-xl border-2 border-dashed border-sakura-300 hover:bg-sakura-50 transition-colors flex items-center justify-center gap-2"
            >
              + Add question manually
            </motion.button>
          </motion.div>
        )}

        {/* Step: Share */}
        {step === "share" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white/80 backdrop-blur-xl rounded-blob shadow-kawaii-lg border-2 border-sakura-200/50 p-6 space-y-5 text-center"
          >
            <Sparkles className="w-12 h-12 text-star mx-auto" />
            <h2 className="text-2xl font-black text-sakura-500">
              Questions saved!
            </h2>
            <p className="text-lavender-400 font-bold text-sm">
              {questions.length} questions in {language} ({questionsPerGame} per game)
            </p>

            {/* QR */}
            <div className="flex justify-center">
              <div className="p-4 bg-white rounded-2xl shadow-kawaii">
                <QRCodeSVG value={shareLink} size={180} level="H" includeMargin fgColor="#E91E6C" />
              </div>
            </div>

            {/* Link */}
            <div className="flex items-center gap-2 bg-sakura-50 rounded-xl p-3 border-2 border-sakura-100">
              <input
                readOnly
                value={shareLink}
                className="flex-1 bg-transparent text-sm font-bold text-gray-600 outline-none truncate"
              />
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => copyLink()}
                className="shrink-0 p-2 bg-sakura-400 text-white rounded-lg"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </motion.button>
            </div>

            <p className="text-xs text-gray-400">
              Share this link to start a game with these questions
            </p>

            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => { setStep("edit"); setShareId(""); }}
                className="flex-1 py-3 text-sm font-bold text-lavender-500 bg-lavender-50 rounded-xl border-2 border-lavender-200"
              >
                Edit Questions
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => { setStep("list"); setQuestions([]); setShareId(""); }}
                className="flex-1 py-3 text-sm font-black text-white bg-gradient-to-r from-sakura-400 to-lavender-400 rounded-xl shadow-kawaii"
              >
                Back to My Sets
              </motion.button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
